import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../auth/authContext';
import { Button, Icon } from '../ui';
import { EntrancesSelect } from './EntrancesSelect';
import { LanguageSelect } from './LanguageSelect';
import { NumbersSelect } from './NumbersSelect';
import { usePreferences } from './preferencesContext';
import { useTelephoneContext } from './telephoneContext';
import { ThemeSelect } from './ThemeSelect';

/**
 * The person's name at the right of the bar, and what is theirs rather than the firm's.
 *
 * The language, the theme and signing out used to sit in the bar as three more controls
 * beside the areas; they are about the person, not about the dispatch, and the bar is the
 * one strip a dispatcher reads all shift. Anything personal that comes later goes here too —
 * never into the firm's settings, which are the administrator's.
 *
 * Opens on the click and closes on Escape or on a click anywhere else, the way `Hint`
 * does. Not a modal: nothing is trapped and the desk behind stays live, because a call can
 * arrive while somebody is choosing a theme.
 */
export function PersonMenu() {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  const telephone = useTelephoneContext();
  const { access } = usePreferences();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    };

    const onPress = (event: MouseEvent) => {
      if (root.current !== null && !root.current.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onPress, true);

    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousedown', onPress, true);
    };
  }, [open]);

  // The name the firm knows them by, then the address they signed in with — and a word
  // rather than nothing for somebody who has neither, which a phone sign-in can be.
  const name = access.displayName ?? access.email ?? t('person.you');

  return (
    <div ref={root} className="relative shrink-0">
      <Button
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((current) => !current)}
      >
        <Icon name="user" small />
        <span className="max-w-[160px] truncate">{name}</span>
        <Icon name="chevron" small />
      </Button>

      {open && (
        <div
          id={id}
          role="group"
          aria-label={t('person.menu')}
          className="absolute right-0 top-full mt-1.5 w-64 rounded-card border border-rule bg-surface text-ink shadow-float z-30"
        >
          <div className="px-3 py-2.5 border-b border-rule">
            <div className="font-semibold truncate">{name}</div>
            {access.email !== null && access.email !== name && (
              <div className="text-secondary text-ink-3 truncate">{access.email}</div>
            )}
          </div>

          <div className="px-3 py-2.5 grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2 border-b border-rule">
            <span className="text-secondary text-ink-2">{t('language.label')}</span>
            <LanguageSelect className="w-full" />
            <span className="text-secondary text-ink-2">{t('theme.label')}</span>
            <ThemeSelect className="w-full" />
            <span className="text-secondary text-ink-2">{t('person.entrances')}</span>
            <EntrancesSelect className="w-full" />
            <span className="text-secondary text-ink-2">{t('person.numbers')}</span>
            <NumbersSelect className="w-full" />
          </div>

          <div className="p-2">
            {/* Leaving the desk first, where desks are chosen, so its telephone stops ringing
                for somebody who has gone home. The PC still remembers it for tomorrow. */}
            <Button
              className="w-full justify-center"
              onClick={() => void telephone.leaveDesk().finally(() => signOut())}
            >
              {t('app.signOut')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
